import type { JobSalary } from "@lossdog/job-watchlists";
import { useEffect, useMemo, useState } from "react";

import { useJobSalaryContext } from "@/data/watchlist/JobSalary.context";

interface UseJobsDataBatchReturn {
  jobsData: (JobSalary | null)[];
  isLoading: boolean;
  errors: (string | null)[];
}

/**
 * Hook to efficiently fetch multiple job data by job IDs
 *
 * @param jobIds - Array of job IDs to fetch
 * @returns Array of job data, loading state, and errors
 */
export const useJobsDataBatch = (jobIds: string[]): UseJobsDataBatchReturn => {
  const { client, isLoading: contextLoading } = useJobSalaryContext();

  const [jobsData, setJobsData] = useState<(JobSalary | null)[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<(string | null)[]>([]);

  // Memoize jobIds array to prevent infinite re-renders
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const memoizedJobIds = useMemo(() => jobIds, [JSON.stringify(jobIds)]);

  // Parse job IDs to extract components
  const parsedJobIds = useMemo(() => {
    return memoizedJobIds.map((jobId) => {
      const parts = jobId.split(".");

      if (parts.length !== 4 || parts[0] !== "job-salary") {
        return null;
      }

      const [, jobSubject, metroAreaId, quantileId] = parts;

      if (!jobSubject || !metroAreaId || !quantileId) {
        return null;
      }

      return {
        jobId,
        jobSubject,
        metroAreaId,
        quantileId,
      };
    });
  }, [memoizedJobIds]);

  useEffect(() => {
    if (contextLoading || !client || memoizedJobIds.length === 0) {
      setJobsData([]);
      setErrors([]);
      return;
    }

    setIsLoading(true);

    const fetchJobsData = async () => {
      const results: (JobSalary | null)[] = [];
      const errorResults: (string | null)[] = [];

      for (let i = 0; i < memoizedJobIds.length; i++) {
        const parsedJobId = parsedJobIds[i];

        if (!parsedJobId) {
          results.push(null);
          errorResults.push("Invalid job ID format");
          continue;
        }

        try {
          const { jobSubject, metroAreaId, quantileId } = parsedJobId;

          // Get metro area and quantile from client
          const metroArea = client.getMetroAreas().find((area) => area.id === metroAreaId);
          const quantile = client.getQuantiles().find((q) => q.id === quantileId);

          if (!metroArea) {
            results.push(null);
            errorResults.push(`Metro area not found: ${metroAreaId}`);
            continue;
          }

          if (!quantile) {
            results.push(null);
            errorResults.push(`Quantile not found: ${quantileId}`);
            continue;
          }

          // Check if job already exists in the client's cache
          const existingJob = client.getOrCreateJobSalary(jobSubject, metroArea, quantile);
          existingJob.subscribe();
          results.push(existingJob);
          errorResults.push(null);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Failed to fetch job data";
          results.push(null);
          errorResults.push(errorMessage);
        }
      }

      setJobsData(results);
      setErrors(errorResults);
      setIsLoading(false);
    };

    fetchJobsData();
  }, [client, memoizedJobIds, parsedJobIds, contextLoading]);

  return {
    jobsData,
    isLoading: isLoading || contextLoading,
    errors,
  };
};
